import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Package, CheckCircle2, Loader, XCircle, Clock, BarChart3, ArrowRight, DollarSign,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip,
} from "recharts";

const fmtAud = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Client-facing dashboard — computed entirely from the customer's own jobs
// (GET /jobs already returns only their jobs), so no extra backend is needed.
const GRANS = [
  { key: "daily", label: "Daily", limit: 30 },
  { key: "weekly", label: "Weekly", limit: 16 },
  { key: "monthly", label: "Monthly", limit: 12 },
  { key: "yearly", label: "Yearly", limit: 6 },
];

function bucketKey(dateStr, gran) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, "0");
  if (gran === "yearly") return String(d.getFullYear());
  if (gran === "monthly") return `${d.getFullYear()}-${p(d.getMonth() + 1)}`;
  if (gran === "weekly") {
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return `${monday.getFullYear()}-${p(monday.getMonth() + 1)}-${p(monday.getDate())}`;
  }
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function bucketLabel(key, gran) {
  try {
    if (gran === "yearly") return key;
    const parts = key.split("-").map(Number);
    if (gran === "monthly") return new Date(parts[0], parts[1] - 1, 1).toLocaleDateString("en-AU", { month: "short", year: "numeric" });
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  } catch { return key; }
}

const STATUS_BREAKDOWN = [
  { key: "needs_approval", label: "Pending Approval", color: "#f59e0b" },
  { key: "assigned", label: "Assigned", color: "#19e6c8" },
  { key: "onboard", label: "In Transit", color: "#a78bfa" },
  { key: "done", label: "Delivered", color: "#22c55e" },
  { key: "futile", label: "Futile", color: "#ef4444" },
];

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [gran, setGran] = useState("monthly");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/jobs");
        if (!cancelled) setJobs(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Normalise legacy "pending" → assigned for counting.
  const norm = (s) => (s === "pending" ? "assigned" : s);

  const counts = useMemo(() => {
    const c = { total: jobs.length, needs_approval: 0, assigned: 0, onboard: 0, done: 0, futile: 0 };
    jobs.forEach((j) => { const s = norm(j.status); if (c[s] != null) c[s] += 1; });
    return c;
  }, [jobs]);

  // Total billed (inc GST) — the authoritative invoice amount when one
  // exists (reflects merges/edits), same fallback GET /jobs already applies
  // server-side for the invoice_ref readout shown elsewhere in the portal.
  const totalSales = useMemo(
    () => jobs.reduce((sum, j) => sum + (j.invoice_ref?.amount != null ? Number(j.invoice_ref.amount) : 0), 0),
    [jobs]
  );

  const kpis = [
    { label: "Total Bookings", value: counts.total, icon: Package, color: "#19e6c8" },
    { label: "Total Sales (inc GST)", value: fmtAud(totalSales), icon: DollarSign, color: "#22c55e" },
    { label: "Delivered", value: counts.done, icon: CheckCircle2, color: "#22c55e" },
    { label: "In Progress", value: counts.assigned + counts.onboard, icon: Loader, color: "#a78bfa" },
    { label: "Pending Approval", value: counts.needs_approval, icon: Clock, color: "#f59e0b" },
    { label: "Futile", value: counts.futile, icon: XCircle, color: "#ef4444" },
  ];

  const granMeta = GRANS.find((g) => g.key === gran);
  const chartData = useMemo(() => {
    const agg = {};
    jobs.forEach((j) => {
      const k = bucketKey(j.job_date || j.created_at, gran);
      if (!k) return;
      if (!agg[k]) agg[k] = { bookings: 0, sales: 0 };
      agg[k].bookings += 1;
      agg[k].sales += j.invoice_ref?.amount != null ? Number(j.invoice_ref.amount) : 0;
    });
    const keys = Object.keys(agg).sort();
    const sliced = keys.slice(Math.max(0, keys.length - granMeta.limit));
    return sliced.map((k) => ({ label: bucketLabel(k, gran), bookings: agg[k].bookings, sales: +agg[k].sales.toFixed(2) }));
  }, [jobs, gran, granMeta]);

  const maxStatus = Math.max(1, ...STATUS_BREAKDOWN.map((s) => counts[s.key] || 0));

  if (loading) return <div className="flex items-center justify-center py-24 text-white/50 text-sm">Loading dashboard…</div>;
  if (err) return <div className="p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200">{String(err)}</div>;

  return (
    <div data-testid="client-dashboard-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="brand-title text-2xl md:text-3xl">Dashboard</h1>
          <p className="text-white/55 text-sm mt-1">Your bookings at a glance — volumes, status and trends.</p>
        </div>
        <button
          onClick={() => navigate("/dashboard/new-request")}
          className="px-4 py-2 rounded-xl text-[#04201c] font-display tracking-widest text-xs font-bold"
          style={{ background: "linear-gradient(90deg, #19e6c8, #0fbfa6)" }}
          data-testid="client-create-cta"
        >
          + Create Job
        </button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-2"
              data-testid={`client-kpi-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-white/50">{s.label}</span>
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${s.color}22`, color: s.color }}>
                  <Icon size={15} />
                </span>
              </div>
              <div className="text-2xl font-bold text-white leading-none mt-1">{s.value}</div>
            </div>
          );
        })}
      </div>

      {/* Bookings over time */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="brand-title text-base flex items-center gap-2">
              <BarChart3 size={15} className="text-[#19e6c8]" /> Jobs Booked
            </h3>
            <p className="text-[11px] text-white/45 mt-0.5">How many jobs you booked over time — switch the period.</p>
          </div>
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
            {GRANS.map((g) => (
              <button key={g.key} type="button" onClick={() => setGran(g.key)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  gran === g.key ? "bg-[#19e6c8]/20 text-[#19e6c8]" : "bg-white/[0.03] text-white/55 hover:bg-white/10 hover:text-white"}`}
                data-testid={`client-gran-${g.key}`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height: 300 }}>
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-white/40 text-sm">No bookings yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 12, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                <Tooltip contentStyle={{ background: "#0b1430", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#fff", fontWeight: 600 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => [v, "Jobs"]} />
                <Bar dataKey="bookings" name="Jobs" fill="#19e6c8" radius={[4, 4, 0, 0]} maxBarSize={42} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Sales over time — same period buckets as Jobs Booked above, so the
          two charts read together (e.g. "quieter month, but higher sales"). */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="mb-4">
          <h3 className="brand-title text-base flex items-center gap-2">
            <DollarSign size={15} className="text-[#22c55e]" /> Sales (incl. GST)
          </h3>
          <p className="text-[11px] text-white/45 mt-0.5">What you were billed over time, by the period selected above.</p>
        </div>
        <div style={{ height: 260 }}>
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-white/40 text-sm">No sales yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 12, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={{ background: "#0b1430", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#fff", fontWeight: 600 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => [fmtAud(v), "Sales"]} />
                <Bar dataKey="sales" name="Sales" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={42} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Status breakdown */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="brand-title text-base">By Status</h3>
          <button onClick={() => navigate("/dashboard/job-status")} className="text-xs uppercase tracking-widest text-white/55 hover:text-white flex items-center gap-1">
            View jobs <ArrowRight size={12} />
          </button>
        </div>
        {counts.total === 0 ? (
          <p className="text-white/40 text-sm">No jobs yet — create your first booking.</p>
        ) : (
          <div className="space-y-2.5">
            {STATUS_BREAKDOWN.map((s) => {
              const n = counts[s.key] || 0;
              const pct = Math.round((n / maxStatus) * 100);
              return (
                <div key={s.key} data-testid={`client-status-${s.key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-white/85">{s.label}</span>
                    <span className="text-white/60 font-mono text-xs">{n}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
